import { useReactiveVar } from '@apollo/client'

import { MissionReviewAreaDarkImage, MissionReviewAreaImage } from '@local/assets/images'

import { darkModeVar } from '@web/hooks/useGlobalDetermineDarkMode'
import nClicks from '@web/libs/nlog/click/codes'

import CompletedMissionCard, { CompletedMissionCardProps } from '../../MissionCard/CompletedMissionCard'
import NoContent from '../../MissionCard/Content/NoContent'
import ProgressStamp from '../../MissionCard/Content/ProgressStamp/ProgressStamp'
import styles from '../../MissionCard/Content/ProgressStamp/ProgressStamp.module.scss'
import { MissionCard, MissionCardProps } from '../../MissionCard/MissionCard'
import ProgressMissionCard, { ProgressMissionCardProps } from '../../MissionCard/ProgressMissionCard'

interface Props {
  missionCard: MissionCardProps
}

const Guide = ({ step }: { step: number }) => {
  if (step === 2) {
    return <>이번 달 완료한 지역 수만큼 추첨에 자동 응모됩니다.</>
  }

  return (
    <>
      지역은 <em>시군구</em> 단위로 집계됩니다.
    </>
  )
}

const RegionReviewMissionCard = ({ missionCard }: Props) => {
  const isDarkMode = useReactiveVar(darkModeVar)

  const stepIndex = MissionCard.getCurrentStepIndex(missionCard) ?? 0

  const getNClickArea = (): ProgressMissionCardProps['nClickArea'] & CompletedMissionCardProps['nClickArea'] => {
    if (stepIndex === 0) {
      return {
        openGuide: nClicks.mission.TIPCARD5,
        closeGuide: nClicks.mission.TIPXCARD5,
        openMore: nClicks.mission.DONECARD5,
        closeMore: nClicks.mission.DONEXCARD5,
      }
    }

    return {
      openGuide: nClicks.mission.TIPCARD9,
      closeGuide: nClicks.mission.TIPXCARD9,
      openPreview: nClicks.mission.MORECARD3,
      closePreview: nClicks.mission.MOREXCARD3,
      openMore: nClicks.mission.DONECARD9,
      closeMore: nClicks.mission.DONEXCARD9,
    }
  }

  // 미션 시작전(미참여)
  if (missionCard.isBeforeStart) {
    return (
      <ProgressMissionCard
        missionCard={missionCard}
        Content={<NoContent image={isDarkMode ? MissionReviewAreaDarkImage : MissionReviewAreaImage} />}
        Guide={<Guide step={stepIndex + 1} />}
        nClickArea={getNClickArea()}
      />
    )
  }

  // 진행중일때 내용 형상
  const ProgressContent = (
    <ProgressStamp
      countTitle={'리뷰'}
      classname={styles.type_reviewer_of_area}
      total={missionCard.itemCountToStamp}
      stamps={missionCard.stampGroups.map(stampGroup =>
        MissionCard.stampGroupToProgressStampItem(
          stampGroup,
          stampGroup.stamps.length >= missionCard.itemCountToStamp
            ? `리뷰 ${missionCard.itemCountToStamp}회 완료`
            : `리뷰 ${stampGroup.stamps.length}회`,
        ),
      )}
      allowOver={stepIndex === 1}
      multiple={stepIndex === 1}
    />
  )

  // 미션 완료
  if (MissionCard.isCompleteMission(missionCard)) {
    const firstCompletedMissionStampGroup = MissionCard.getFirstCompleteMissionStampGroup(missionCard)

    const Content = firstCompletedMissionStampGroup && (
      <ProgressStamp
        countTitle={'리뷰'}
        classname={styles.type_reviewer_of_area}
        total={missionCard.itemCountToStamp}
        stamps={[
          MissionCard.stampGroupToProgressStampItem(
            firstCompletedMissionStampGroup,
            `리뷰 ${missionCard.itemCountToStamp}번 쓰기 완료`,
          ),
        ]}
      />
    )

    return (
      <CompletedMissionCard
        missionCard={missionCard}
        Content={Content ?? ProgressContent}
        foldable
        nClickArea={getNClickArea()}
      />
    )
  }
  // 미션 진행중
  else {
    return (
      <ProgressMissionCard
        Guide={<Guide step={stepIndex + 1} />}
        missionCard={missionCard}
        Content={ProgressContent}
        nClickArea={getNClickArea()}
      />
    )
  }
}

export default RegionReviewMissionCard
